import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { date } from '../lib/format';
import { supabase } from '../lib/supabase';

interface Comment { id: string; body: string; created_at: string; author_id: string | null; author_name: string | null; is_internal: boolean; users?: { full_name: string } | null }
interface Activity { event: string; payload: any; created_at: string }

const EVENT_LABEL: Record<string, string> = {
  created: 'Quote created', edited: 'Edited', emailed: 'Sent to customer', viewed: 'Viewed by customer',
  accepted_online: 'Accepted online', customer_comment: 'Customer commented', converted_to_job: 'Converted to job',
  status_changed: 'Status changed',
};

export default function QuoteComments({ quoteId, refreshKey }: { quoteId: string; refreshKey: number }) {
  const { profile } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [c, a] = await Promise.all([
      supabase.from('comments').select('*, users:author_id(full_name)').eq('entity_type', 'quote').eq('entity_id', quoteId)
        .eq('archived', false).order('created_at'),
      supabase.from('activity_log').select('event, payload, created_at').eq('entity_type', 'quote').eq('entity_id', quoteId)
        .neq('event', 'edited').order('created_at', { ascending: false }).limit(15),
    ]);
    setComments((c.data as Comment[]) ?? []);
    setActivity((a.data as Activity[]) ?? []);
  }, [quoteId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const post = async () => {
    if (!body.trim()) return;
    setBusy(true);
    await supabase.from('comments').insert({ entity_type: 'quote', entity_id: quoteId, body: body.trim(),
      author_id: profile?.id, is_internal: internal, created_by: profile?.id });
    setBusy(false);
    setBody('');
    load();
  };

  return (
    <>
      <div className="card card-pad">
        <h3>Comments</h3>
        {comments.length === 0 && <div className="muted small" style={{ marginBottom: 8 }}>No comments yet.</div>}
        {comments.map((c) => (
          <div key={c.id} className={`comment ${c.author_id ? '' : 'customer'} ${c.is_internal ? 'internal' : ''}`}>
            <div className="small muted">
              {c.author_id ? c.users?.full_name ?? 'Staff' : `${c.author_name} (customer)`} · {date(c.created_at)}
              {c.is_internal && ' · internal'}
            </div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{c.body}</div>
          </div>
        ))}
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder={internal ? 'Internal note (customer won’t see it)' : 'Reply — the customer sees this on their quote page'} />
        <div className="row" style={{ marginTop: 6 }}>
          <label className="check small"><input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} /> Internal only</label>
          <span className="grow" />
          <button className="small" onClick={post} disabled={busy || !body.trim()}>Post</button>
        </div>
      </div>
      {activity.length > 0 && (
        <div className="card card-pad">
          <h3>Activity</h3>
          {activity.map((a, i) => (
            <div key={i} className="small" style={{ padding: '2px 0' }}>
              <span className="muted">{date(a.created_at)}</span> · {EVENT_LABEL[a.event] ?? a.event}
              {a.payload?.name && <> — {a.payload.name}</>}
              {a.event === 'emailed' && a.payload?.to && <> — {a.payload.to}</>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
